<template>
	<div class="credit-update-form-container">
		<Form ref="updateForm" @submit="handleSubmit">
			<!-- Credit Type Display (Read-only) -->
			<div class="col-md-12 mb-3" v-if="canSelect">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-start align-items-center mb-2">
					<label class="form-label fw-normal col-12 col-md-6">Credit Type</label>
					<div class="col-12 col-md-6">
						<SelectField
							id="creditType"
							name="creditType"
							label="Select credit type"
							v-model="formData.creditType"
							:options="creditTypeOptions"
							rules="required"
							ref="creditTypeSelect"
						>
                        <option v-for="type in creditTypes" :key="type.name" :value="type.value">{{ type.name }}</option>
                        </SelectField>
					</div>
				</div>
			</div>

			<!-- Total Credit Amount -->
			<div class="col-md-12">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-start align-items-center mb-2">
					<label class="form-label fw-normal col-12 col-md-6">Total Credit Amount</label>
					<div class="col-12 col-md-6">
						<MoneyField 
							id="totalAmount"
							name="totalAmount"
							label="Input total amount"
							v-model="formData.totalAmount"
							currency="NGN"
							placeholder="₦0.00"
							locale="en-NG"
							@change="calculateRepayments"
							rules="required"
						/>
					</div>
				</div>
			</div>

			<!-- Repayment per Swap -->
			<div class="col-md-12">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-center align-items-center mb-2">
					<label class="form-label fw-normal col-12 col-md-6">Repayment per Swap</label>
					<div class="col-12 col-md-6">
						<MoneyField 
							id="repaymentPerSwap"
							name="repaymentPerSwap"
							label="Input repayment per swap"
							v-model="formData.repaymentPerSwap"
							currency="NGN"
							placeholder="₦0.00"
							locale="en-NG"
							@change="calculateRepayments"
							rules="required"
						/>
					</div>
				</div>
			</div>

			<!-- Number of Repayments -->
			<div class="col-md-12">
				<div class="w-100 d-flex flex-column flex-md-row justify-content-center align-items-center mb-2">
					<label class="form-label fw-normal col-12 col-md-6">Number of Repayments</label>
					<div class="col-12 col-md-6">
						<InputField 
							id="numberOfRepayments"
							name="numberOfRepayments" 
							class="form-control" 
							v-model="formData.numberOfRepayments"
							label="Number of repayments"
							:disabled="true"
						/>
					</div>
				</div>
			</div>

			<!-- Credit Agreement Upload -->
			<div class="col-md-12 mt-3">
				<div class="border-bottom pb-2 mb-3">
					<h6 class="fw-bold mb-1">Credit Agreement</h6>
					<p class="small text-muted mb-0">Upload a new signed credit agreement to replace the existing one.</p>
				</div>

				<!-- Existing document preview -->
				<div v-if="existingAgreement && !formData.credit_agreement" class="border rounded-3 p-3 bg-white d-flex align-items-center justify-content-between gap-3 mb-3">
					<div class="d-flex align-items-center gap-3">
						<div class="rounded-2 bg-light border d-flex align-items-center justify-content-center" style="width:40px; height:40px; flex-shrink:0;">
							<i class="fas fa-file-alt text-secondary"></i>
						</div>
						<div>
							<div class="fw-semibold" style="font-size:0.85rem;">Current Agreement</div>
							<div class="text-muted" style="font-size:0.75rem;">{{ existingAgreement.name }}</div>
						</div>
					</div>
					<a :href="existingAgreement.url" target="_blank" class="btn btn-sm btn-outline-secondary"><i class="fas fa-eye me-1"></i> View</a>
				</div>

				<!-- New upload (shown after clearing, or if no existing doc) -->
				<div v-if="formData.credit_agreement" class="upload-done mb-3">
					<div class="d-flex align-items-center gap-2">
						<i class="fa fa-check-circle text-success"></i>
						<div>
							<div class="upload-done-name">Credit Agreement</div>
							<div class="upload-done-sub">New document ready to upload</div>
						</div>
					</div>
					<button type="button" class="btn btn-sm p-0 text-danger" @click="formData.credit_agreement = undefined"><i class="fa fa-times"></i></button>
				</div>

				<div v-if="!formData.credit_agreement">
					<Dropzone param="data" name="credit_agreement" v-model="formData.credit_agreement" label="Upload New Credit Agreement" :url="route('upload.ajax')" class="dropzone-field" />
				</div>
			</div>

			<!-- Current Progress Info -->
			<div class="col-md-12 mb-3">
				<div class="alert alert-info border-0">
					<div class="d-flex justify-content-between mb-2">
						<span class="fw-semibold">Paid Repayments:</span>
						<span>{{ creditData.paidSequence }} / {{ creditData.numberOfRepayments }}</span>
					</div>
					<div class="d-flex justify-content-between mb-2">
						<span class="fw-semibold">Current Balance:</span>
						<span>{{ creditData.balance?.formatted || '₦0.00' }}</span>
					</div>
					<div class="progress" style="height: 8px;">
						<div 
							class="progress-bar bg-success" 
							role="progressbar" 
							:style="{width: progressPercentage + '%'}"
							:aria-valuenow="progressPercentage" 
							aria-valuemin="0" 
							aria-valuemax="100"
						></div>
					</div>
					<small class="text-muted mt-1 d-block">{{ progressPercentage }}% completed</small>
				</div>
			</div>

			<!-- Warning Message -->
			<div class="col-md-12 mb-3" v-if="showWarning">
				<div class="alert alert-warning border-0">
					<i class="fas fa-exclamation-triangle me-2"></i>
					<strong>Warning:</strong> Once a customer begins repayment on a credit plan,
					 the system disables all update actions.
					  Any change requests must go through credit closure and reissuance.
				</div>
			</div>

			<!-- Action Buttons -->
			<div class="col-md-12">
				<div class="d-flex gap-2 justify-content-end mt-3">
					<button 
						type="button" 
						class="btn btn-outline-danger px-4" 
						@click="handleCancel"
						:disabled="isLoading"
					>
						Cancel
					</button>
					<button 
						type="submit" 
						class="btn bg-secondary text-light px-4" 
						:disabled="!canSubmit || isLoading"
					>
						<span v-if="isLoading">
							<i class="fas fa-spinner fa-spin me-2"></i>Updating...
						</span>
						<span v-else>
							<i class="fas fa-save me-2"></i>Update Credit Plan
						</span>
					</button>
				</div>
			</div>
		</Form>
	</div>
</template>

<script lang="ts">
import $ from 'jquery'
import { Component, Prop, Vue, toNative, Emit, Watch, Ref } from 'vue-facing-decorator'
import Form from '@/vue/components/Form.vue'
import InputField from '@/vue/components/InputField.vue'
import MoneyField from '@/vue/components/MoneyField.vue'
import type { Options as Select2Options } from 'select2'
import SelectField from '@/vue/components/SelectField.vue'
import Dropzone from '@/widget/Dropzone.vue'


interface CreditUpdateData {
	id: string | number
	type?: string
	totalAmount: number
	repaymentPerSwap: number
	numberOfRepayments: number
	paidSequence: number
	balance?: {
		amount: string
		currency: string
		formatted: string
	}
}

interface UpdateFormData {
	totalAmount: number
	repaymentPerSwap: number
	numberOfRepayments: number
    creditType: string
	credit_agreement?: string
}

@Component({
	components: {
		Form,
		InputField,
		MoneyField,
        SelectField,
		Dropzone
	}
})
class CreditUpdateForm extends Vue {
	@Prop({ type: String, required: true })
	readonly url!: string

	@Prop({ type: Object, required: true })
	readonly creditData!: CreditUpdateData

	@Prop({ type: [String, Number], required: true })
	readonly riderId!: string | number

    @Prop({ type: Array, default: () => [] })
	readonly creditTypes!: any

    	@Prop({ type: Boolean, default: false })
    readonly canSelect!: boolean

	@Prop({ type: Object, default: null })
	readonly existingAgreement!: { id: number, url: string, name: string } | null

	@Ref('creditTypeSelect') creditTypeSelect!: any

  

	internalLoading = false

	formData: UpdateFormData = {
		totalAmount: 0,
		repaymentPerSwap: 0,
		numberOfRepayments: 0,
        creditType: '',
		credit_agreement: undefined
	}

	
	get creditTypeOptions(): Select2Options {
		return {
			placeholder: 'Select Credit Type',
			minimumResultsForSearch: Infinity,
			ajax:null
		}
	}

	get progressPercentage(): number {
		if (!this.creditData.numberOfRepayments) return 0
		return Math.round((this.creditData.paidSequence / this.creditData.numberOfRepayments) * 100)
	}

	get showWarning(): boolean {
		// Show warning if there are unpaid repayments
		return this.creditData.paidSequence < this.creditData.numberOfRepayments
	}

	get canSubmit(): boolean {
		const hasRequiredFields = this.formData.totalAmount > 0 && 
			this.formData.repaymentPerSwap > 0 && 
			this.formData.numberOfRepayments > 0

		return hasRequiredFields
	}

	get isLoading(): boolean {
		return this.internalLoading
	}

	getOriginalAmount(field: 'totalAmount' | 'repaymentPerSwap'): number {
		// Convert from kobo to naira for comparison
		const amount = this.creditData[field]
		if (typeof amount === 'object' && 'amount' in amount) {
			return parseFloat(amount.amount) / 100
		}
		return 0
	}

	get calculateRepayments() {
		if (this.formData.totalAmount > 0 && this.formData.repaymentPerSwap > 0) {
			this.formData.numberOfRepayments = Math.ceil(
				this.formData.totalAmount / this.formData.repaymentPerSwap
			)
		} else {
			this.formData.numberOfRepayments = 0
		}
        return this.formData.numberOfRepayments
	}

	async handleSubmit(payload: any) {
		try {
			this.setLoading(true)

			const submissionData: any = {
				...payload,
				rider: this.riderId,
				creditId: this.creditData.id,
				totalAmount: payload.totalAmount * 100,
				repaymentPerSwap: payload.repaymentPerSwap * 100,
				numberOfRepayments: payload.numberOfRepayments,
			}

			if (this.formData.creditType) {
				submissionData.creditType = this.formData.creditType
			}

			if (this.formData.credit_agreement) {
				submissionData.credit_agreement = this.formData.credit_agreement
			}
			const response = await this.$http.put(this.url, submissionData)

			this.onSuccess(response)
			

		} catch (error) {
			this.onError(error)
		} finally {
			this.setLoading(false)
		}
	}

	@Emit('success')
    onSuccess(response: any) {
        
        const eventData = {
            message: response?.data?.message || 'Credit plan updated successfully!',
            data: response?.data
        }

     
		

        return eventData
    }

	@Emit('error')
	onError(error: any) {
		return {
			message: error.response?.data?.message || 'Failed to update credit plan. Please try again.',
			error
		}
	}

    setLoading(state: boolean) {
		return this.internalLoading = state
	}

	@Emit('cancel')
	handleCancel() {
		return true
	}

	@Watch('creditData', { immediate: true, deep: true })
	onCreditDataChange() {
		this.initializeForm()
	}

	initializeForm() {
    if (this.creditData) {
        this.formData.totalAmount = this.getOriginalAmount('totalAmount')
        this.formData.repaymentPerSwap = this.getOriginalAmount('repaymentPerSwap')
        this.formData.numberOfRepayments = this.creditData.numberOfRepayments
        
        if (this.creditData.credit_type) {
            const currentLabel = String(this.creditData.credit_type).trim();
            
            const matchedType = this.creditTypes.find((type: { value: string, name: string }) => {
                return type.name.toLowerCase() === currentLabel.toLowerCase();
            });
            
            if (matchedType) {
                this.formData.creditType = matchedType.value;
            } else {
                this.formData.creditType = '';
            }
        } else {
            this.formData.creditType = '';
        }
    }
}

	created() {
		this.initializeForm()
	}
}

export default toNative(CreditUpdateForm)
</script>

<style scoped>
.credit-update-form-container {
	width: 100%;
}

.form-label {
	margin-bottom: 0.5rem;
}

.bg-light {
	background-color: #f8f9fa !important;
}

.progress {
	background-color: #e9ecef;
}

.alert {
	border-radius: 0.5rem;
}

@media (max-width: 768px) {
	.col-md-6 {
		margin-bottom: 0.5rem;
	}
}
</style>